import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  notificationService,
  type Notification,
  type Invitation
} from '../services/notificationService'
import { useAuthStore } from './auth'

const POLL_INTERVAL_MS = 30_000

export const useNotificationStore = defineStore('notifications', () => {
  // ── State ────────────────────────────────────────────────────────────────
  const notifications = ref<Notification[]>([])
  const allNotifications = ref<Notification[]>([])
  const allTotalPages = ref(1)
  const unreadCount = ref(0)
  const pendingInvitations = ref<Invitation[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  let pollingTimer: ReturnType<typeof setInterval> | null = null

  // ── Actions ──────────────────────────────────────────────────────────────

  async function fetchUnreadCount() {
    try {
      const data = await notificationService.getNotifications({ is_read: false, limit: 1 })
      unreadCount.value = data.unread_count
    } catch {
      // Silently fail for background polling — don't disrupt the UI
    }
  }

  async function fetchLatestNotifications() {
    try {
      const data = await notificationService.getNotifications({ limit: 10 })
      notifications.value = data.notifications
      unreadCount.value = data.unread_count
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load notifications'
    }
  }

  async function fetchAllNotifications(page = 1, isRead?: boolean) {
    isLoading.value = true
    error.value = null
    try {
      const params = { page, limit: 20, ...(isRead !== undefined ? { is_read: isRead } : {}) }
      const data = await notificationService.getNotifications(params)
      allNotifications.value = data.notifications
      allTotalPages.value = data.total_pages
      unreadCount.value = data.unread_count
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load notifications'
    } finally {
      isLoading.value = false
    }
  }

  async function markAsRead(id: number) {
    try {
      await notificationService.markAsRead(id)
      // Update local state for both lists
      const update = (list: Notification[]) => {
        const n = list.find(n => n.id === id)
        if (n && !n.is_read) {
          n.is_read = true
          unreadCount.value = Math.max(0, unreadCount.value - 1)
        }
      }
      update(notifications.value)
      update(allNotifications.value)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to mark notification as read'
    }
  }

  async function markAllAsRead() {
    try {
      await notificationService.markAllAsRead()
      notifications.value.forEach(n => { n.is_read = true })
      allNotifications.value.forEach(n => { n.is_read = true })
      unreadCount.value = 0
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to mark all notifications as read'
    }
  }

  async function clearAllNotifications() {
    try {
      await notificationService.deleteAllNotifications()
      notifications.value = []
      allNotifications.value = []
      unreadCount.value = 0
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to clear notifications'
      throw e
    }
  }

  async function fetchPendingInvitations() {
    try {
      const data = await notificationService.getInvitations('pending')
      pendingInvitations.value = data.invitations
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load invitations'
    }
  }

  async function acceptInvitation(id: number) {
    try {
      await notificationService.acceptInvitation(id)
      pendingInvitations.value = pendingInvitations.value.filter(inv => inv.id !== id)
      // Reload user groups since membership changed
      await useAuthStore().loadUserData()
      // Refresh notifications to reflect the change
      await fetchLatestNotifications()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to accept invitation'
      throw e
    }
  }

  async function declineInvitation(id: number) {
    try {
      await notificationService.declineInvitation(id)
      pendingInvitations.value = pendingInvitations.value.filter(inv => inv.id !== id)
      await fetchLatestNotifications()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to decline invitation'
      throw e
    }
  }

  function startPolling() {
    if (pollingTimer !== null) return
    // Fire immediately, then on interval
    fetchUnreadCount()
    pollingTimer = setInterval(fetchUnreadCount, POLL_INTERVAL_MS)
  }

  function stopPolling() {
    if (pollingTimer !== null) {
      clearInterval(pollingTimer)
      pollingTimer = null
    }
  }

  return {
    notifications,
    allNotifications,
    allTotalPages,
    unreadCount,
    pendingInvitations,
    isLoading,
    error,
    fetchUnreadCount,
    fetchLatestNotifications,
    fetchAllNotifications,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
    fetchPendingInvitations,
    acceptInvitation,
    declineInvitation,
    startPolling,
    stopPolling
  }
})
